import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';

import { useAppDispatch, useAppSelector } from '@src/core/hooks';
import { SaleChangeStatusEnum } from '@src/core/buyerApiClient';
import { getLastRescheduleSaleChange, shouldShowSaleChangeStatus, type SaleChange } from '@src/domains/sale-changes';
import {
  resolveRescheduleAvailability,
  shouldShowReservationActions,
  type DetailedSaleItem,
} from '@src/domains/sale-items';
import { refreshOrderAsCurrent, refreshOrderPageContainingItem } from '@src/features/orders';
import {
  loadRescheduleAvailableDates,
  saleItemRescheduleOngoingSelector,
  rescheduleErrorKeysSelector,
} from '@src/stores/sale';
import {
  showAppliedFeedbackForSaleChangeIdSelector,
  setShowAppliedFeedbackForSaleChange,
} from '@src/stores/saleChange';

interface Params {
  saleItem: DetailedSaleItem;
  orderId?: string;
  isPackageItem?: boolean;
  hasMultipleVouchers: boolean;
  hasVoucherSplitPolicy: boolean;
  onSaleChangeCheckout?: (saleChange: SaleChange) => void;
}

const initialState = { isDone: false, isGoing: false, isError: false, errorKeys: [] as string[] };

export function useReservationRescheduling({
  saleItem,
  orderId,
  isPackageItem = false,
  hasMultipleVouchers,
  hasVoucherSplitPolicy,
  onSaleChangeCheckout,
}: Params) {
  const dispatch = useAppDispatch();
  const [state, setState] = useState(initialState);

  const isRescheduleOngoing = useAppSelector(saleItemRescheduleOngoingSelector);
  const rescheduleErrorKeys = useAppSelector(rescheduleErrorKeysSelector);
  const appliedFeedbackSaleChangeId = useAppSelector(showAppliedFeedbackForSaleChangeIdSelector);

  const availability = useMemo(
    () =>
      resolveRescheduleAvailability({
        canReschedule: saleItem.rescheduleInfo.canReschedule,
        isRescheduleInProgress: state.isGoing,
        isPackageItem,
      }),
    [saleItem.rescheduleInfo.canReschedule, state.isGoing, isPackageItem],
  );

  const lastSaleChange = useMemo(() => getLastRescheduleSaleChange(saleItem), [saleItem]);

  const showsStatus = useMemo(
    () =>
      shouldShowSaleChangeStatus({
        lastSaleChange,
        isRescheduleDone: state.isDone,
        appliedFeedbackSaleChangeId,
      }),
    [lastSaleChange, state.isDone, appliedFeedbackSaleChangeId],
  );

  const showsActions = useMemo(
    () =>
      shouldShowReservationActions({
        hasMultipleVouchers,
        hasVoucherSplitPolicy,
        canShowReschedule: availability.isVisible,
        lastSaleChangeStatus: lastSaleChange?.status,
      }),
    [hasMultipleVouchers, hasVoucherSplitPolicy, availability.isVisible, lastSaleChange?.status],
  );

  useEffect(() => {
    const hasErrors = rescheduleErrorKeys.length > 0 && !isRescheduleOngoing;

    setState((current) => ({
      ...current,
      isError: hasErrors,
      errorKeys: hasErrors ? rescheduleErrorKeys : [],
    }));
  }, [rescheduleErrorKeys, isRescheduleOngoing]);

  const cancel = useCallback(() => setState(initialState), []);

  const confirm = useCallback(async () => {
    dispatch(refreshOrderPageContainingItem(saleItem.id));

    await dispatch(refreshOrderAsCurrent(orderId ?? ''));

    setState({ isDone: true, isGoing: false, isError: false, errorKeys: [] });
  }, [dispatch, saleItem.id, orderId]);

  const start = useCallback(() => {
    setState((current) => ({ ...current, isGoing: true }));
    dispatch(loadRescheduleAvailableDates(saleItem.id));
  }, [dispatch, saleItem.id]);

  const dismissApplied = useCallback(() => {
    if (!lastSaleChange?.id || lastSaleChange.status !== SaleChangeStatusEnum.Applied) return;

    dispatch(setShowAppliedFeedbackForSaleChange(null));

    if (state.isDone) {
      setState((s) => ({ ...s, isDone: false }));
    }
  }, [dispatch, lastSaleChange?.id, lastSaleChange?.status, state.isDone]);

  useLayoutEffect(() => {
    if (state.isDone) {
      const saleChange = saleItem.saleChanges.find((change) => change.status === SaleChangeStatusEnum.Pending);

      if (saleChange && onSaleChangeCheckout) {
        onSaleChangeCheckout(saleChange);
      }
    }
  }, [state.isDone, saleItem.saleChanges, onSaleChangeCheckout]);

  useEffect(() => {
    return () => {
      if (
        appliedFeedbackSaleChangeId &&
        lastSaleChange &&
        lastSaleChange.id === appliedFeedbackSaleChangeId &&
        lastSaleChange.status === SaleChangeStatusEnum.Applied
      ) {
        dispatch(setShowAppliedFeedbackForSaleChange(null));
      }
    };
  }, [appliedFeedbackSaleChangeId, lastSaleChange]);

  return {
    state,
    isRescheduleOngoing,
    availability,
    lastSaleChange,
    showsStatus,
    showsActions,
    start,
    cancel,
    confirm,
    dismissApplied,
  };
}
